import React from "react"

export const FORGOT_PASSWORD_INITIAL_STEP = 0
export const FORGOT_PASSWORD_VALIDATION_STEP = 1

export enum ForgotPasswordActionType {
	INIT = "INIT",
	VALIDATE = "VALIDATE",
}

interface ForgotPasswordAction {
	type: ForgotPasswordActionType
	payload?: any
}

interface ForgotPasswordState {
	step: number
	data: any
}

type ForgotPasswordProcessContextInterface = {
	forgotPasswordState: ForgotPasswordState
	forgotPasswordDispatch: React.Dispatch<ForgotPasswordAction>
}

const ForgotPasswordProcessContext = React.createContext<
	ForgotPasswordProcessContextInterface | undefined
>(undefined)

const forgotPasswordProcessReducer = (
	forgotPasswordState: ForgotPasswordState,
	action: ForgotPasswordAction
) => {
	const { type, payload } = action
	switch (type) {
		case "INIT": {
			return { step: FORGOT_PASSWORD_INITIAL_STEP, data: payload || null }
		}
		case "VALIDATE": {
			return {
				step: FORGOT_PASSWORD_VALIDATION_STEP,
				data: payload || null,
			}
		}
		default:
			return forgotPasswordState
	}
}

/**********************
 ** Context provider **
 **********************/

// prettier-ignore
const ForgotPasswordProcessProvider = ({ children }: { children: React.ReactNode }) => {
	const [forgotPasswordState, forgotPasswordDispatch] = React.useReducer(
		forgotPasswordProcessReducer,
		{
			step: FORGOT_PASSWORD_INITIAL_STEP,
			data: null,
		}
	)
	return (
		<ForgotPasswordProcessContext.Provider
			value={{ forgotPasswordState, forgotPasswordDispatch }}
		>
			{children}
		</ForgotPasswordProcessContext.Provider>
	)
}

/**********************
 ** Context consumer **
 **********************/
// The context consumer uses the render props pattern
// to directly provide a consumer component with the value to consume

type ChildrenRenderProps = {
	// prettier-ignore
	children: ({ forgotPasswordState } : {forgotPasswordState: ForgotPasswordState}) => React.ReactChild
}

// prettier-ignore
const ForgotPasswordProcessConsumer = ({ children }: ChildrenRenderProps) => (
	<ForgotPasswordProcessContext.Consumer>
		{({ forgotPasswordState }) => children({ forgotPasswordState })}
	</ForgotPasswordProcessContext.Consumer>
)

/**********************
 ** Context hook **
 **********************/

const useForgotPasswordProcess = () => {
	const context = React.useContext(ForgotPasswordProcessContext)
	if (context === undefined) {
		throw new Error(
			"useForgotPasswordProcess must be used within a ForgotPasswordProcessProvider"
		)
	}
	return context
}

export { useForgotPasswordProcess, ForgotPasswordProcessProvider, ForgotPasswordProcessConsumer }
